import React from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import { SubscriptionTypeTypes } from '@/modules/base/util/constants'
import DefaultTrialModal from '@/modules/base/components/modal/trial-modal/trial-modal-default'
import { ModalBig } from '@/modules/base/components/modal'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import DocumentLogoIcon from '@assets/images/product/documents_white_with_icon.svg'
import { SubTitle, Title } from '@/modules/base/components/texts'
import trialBannerUrl from '@assets/images/document/trial-banner.svg?url'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import SVGIconHand from '@assets/images/document/hand.svg?url'
import SVGIconBrain from '@assets/images/document/brain.svg?url'
import SVGIconClock from '@assets/images/document/clock.svg?url'
import SVGIconDocuments from '@assets/images/document/documents.svg?url'
import ThemeInterface from '@/theme-interface'
import { TrialModalProps } from '@/modules/base/components/modal/trial-modal/trial-modal-props'

const icons = {
  'hand': <SVGIcon svg={SVGIconHand} width={'48px'} />,
  'brain': <SVGIcon svg={SVGIconBrain} width={'48px'} />,
  'clock': <SVGIcon svg={SVGIconClock} width={'48px'} />,
  'documents': <SVGIcon svg={SVGIconDocuments} width={'48px'} />,
}
const useStyles = makeStyles((theme: ThemeInterface) => ({
  modal: {
    background: '#1F2E53',
    padding: theme.spacing(0),
    height: '800px',
    borderRadius: 32,
  },
  container: {
    display: 'flex',
    color: theme.white,
    height: '100%',
    width: '100%',
  },
  logo: {
    width: 'fit-content',
  },
  right: {
    background: '#1F2E53',
    width: '560px',
    padding: '80px 80px 80px 48px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  left: {
    background: theme.backgroundDarkBlue,
    width: '560px',
    borderRadius: 32,
    padding: theme.spacing(10), // 80px
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6), // '48px',
  },
  title: {
    marginBottom: 0,
    width: '90%',
    fontSize: '40px',
    lineHeight: '56px',
  },
  subtitle: {
    fontSize: theme.fontSizes.default,
    lineHeight: theme.fontSizes.xlarge,
    color: theme.white,
  },
  bulletPointContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2), // '16px',
  },
  bulletPointContainerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(3), // 24px
    height: '120px',
  },
  bulletPoint: {
    margin: '4px 0',
  },
  bulletPointText: {
    fontSize: theme.fontSizes.smedium,
    lineHeight: theme.fontSizes.xlarge,
    fontWeight: theme.fontWeights.regular,
    textAlign: 'left',
  },
  bulletPointSubText: {
    fontSize: theme.fontSizes.default,
    lineHeight: theme.fontSizes.xlarge,
    fontWeight: theme.fontWeights.light,
  },
  buttonContainer: {
    display: 'flex',
    padding: '0px 24px 0px 24px',
  },
  cancelButton: {
    background: 'transparent',
    border: 'none',
    color: '#84b9f5',
    transition: ' 0.1s',
    '&:hover': {
      color: '#a7d4ff',
      background: 'transparent !important',
    },
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  image: {
    maxHeight: '100%',
  },
}))

const BulletPoint = ({ sentence }) => {
  const classes = useStyles()

  return (
    <div className={classes.bulletPointContainerRow}>
      {icons[sentence.icon]}
      <div className={classes.bulletPoint}>
        <div className={classes.bulletPointText}>
          {sentence.text}
        </div>
        <div className={classes.bulletPointSubText}>
          {sentence.subText}
        </div>
      </div>
    </div>
  )
}

const TrialModalOmo = (props: TrialModalProps) => {
  const { open, productType, dataQsId, onClose, onStartTrial } = props
  const classes = useStyles()

  return productType === SubscriptionTypeTypes.Document
    ? (
      <ModalBig
        open={open}
        onClose={onClose}
        className={classes.modal}
        showCloseIcon={false}
        width={1120}
        dataQsId={dataQsId}
      >
        <div className={classes.container}>
          <div className={classes.left}>
            <div className={classes.textContainer}>
              <SVGIcon className={classes.logo} svg={DocumentLogoIcon} height={45} fill={'#fff'} />
              <Title
                size='large'
                className={classes.title}>{lang.myAccount.subscriptionUpgrade.documentTrialModal.title}</Title>
              <SubTitle
                customClass={classes.subtitle}>{lang.myAccount.subscriptionUpgrade.documentTrialModal.subTitle}</SubTitle>
            </div>
            <div className={classes.imageContainer}>
              <SVGIcon
                svg={trialBannerUrl} className={classes.image} fill={'#A2C62E'}
                width={'400px'} height={'240px'} />
            </div>
          </div>
          <div className={classes.right}>
            <div className={classes.bulletPointContainer}>
              {lang.myAccount.subscriptionUpgrade.documentTrialModal.bulletPoints.map((sentence, key) =>
                <BulletPoint sentence={sentence} key={key} />,
              )}
            </div>
            <div className={classes.buttonContainer}>
              <ButtonIconSquare
                dataQsId={`${dataQsId}-confirm`}
                key='confirm'
                type='submit'
                label={lang.myAccount.subscriptionUpgrade.documentTrialModal.confirmButtonLabel}
                onClick={onStartTrial}
              />
              <CancelButton
                dataQsId={`${dataQsId}-cancel`}
                key='cancel'
                type='cancel'
                customClass={classes.cancelButton}
                label={lang.myAccount.subscriptionUpgrade.documentTrialModal.cancelButtonLabel}
                onClick={onClose}
              />
            </div>
          </div>
        </div>
      </ModalBig>
    )
    : (
      <DefaultTrialModal
        open={open}
        dataQsId={dataQsId}
        onStartTrial={onStartTrial}
        onClose={onClose}
        productType={productType} />
    )
}

export default TrialModalOmo
